<template>
  <div class="homepage">
    <div class="background-particles" ref="particles"></div>

    <section class="hero starfield" id="home" ref="starfieldContainer">
      <div class="hero-content">
        <div class="hero-logo">
          <div class="logo-icon">
            <div class="logo-cube">
              <div class="cube-face"></div>
              <div class="cube-face"></div>
              <div class="cube-face"></div>
              <div class="cube-face"></div>
              <div class="cube-face"></div>
              <div class="cube-face"></div>
            </div>
          </div>
        </div>
        <h1 class="hero-title">CC輔助商城</h1>
        <p class="hero-subtitle">頂級遊戲輔助工具，提升您的遊戲體驗</p>

        <div class="hero-stats">
          <div class="stat-item">
            <span class="stat-number">{{ heroStats.userCount }}</span>
            <span class="stat-label">活躍用戶</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">{{ heroStats.pluginCount }}</span>
            <span class="stat-label">外掛產品</span>
          </div>
          <div class="stat-item">
            <span class="stat-number">{{ heroStats.satisfactionRate }}%</span>
            <span class="stat-label">滿意度</span>
          </div>
        </div>

        <div class="hero-cta">
          <a href="https://lin.ee/GwsPMQw" target="_blank" class="cta-button">
            <span class="button-icon">💬</span>
            <span>加入官方LINE</span>
            <span class="button-arrow">→</span>
          </a>
        </div>
      </div>

      <div class="scroll-indicator">
        <div class="scroll-arrow"></div>
      </div>
    </section>

    <section class="products-section" id="products">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">熱門商品</h2>
          <div class="title-decoration"></div>
          <p class="section-subtitle">精選高品質遊戲外掛，助您稱霸遊戲世界</p>
        </div>
      </div>

      <div class="products-grid-fullscreen-split">
        <div
          v-for="(product, index) in products"
          :key="index"
          class="product-card"
          :style="{ animationDelay: index * 0.1 + 's' }"
          :class="{ 'is-active': activeProductIndex === index }"
          @click="toggleProductActive(index)"
        >
          <img :src="product.image" :alt="product.title" class="product-media-bg product-media-image" loading="lazy" />
          <video :ref="el => { if (el) videoRefs[index] = el }" :src="product.video" class="product-media-bg product-media-video" muted loop playsinline></video>
          <div class="product-content-overlay">
            <h3 class="product-title">{{ product.title }}</h3>
            <p class="product-description">{{ product.description }}</p>
            <ul class="product-features">
              <li v-for="(feature, idx) in product.features.slice(0, 4)" :key="idx">
                <span class="feature-check">✓</span>
                {{ feature }}
              </li>
            </ul>
            <div class="product-footer">
              <button class="product-btn" @click.stop="gotoLine">了解更多</button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="features-section" id="features">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">核心優勢</h2>
          <div class="title-decoration"></div>
          <p class="section-subtitle">為什麼選擇我們的外掛產品</p>
        </div>

        <div class="features-grid">
          <div
            v-for="(feature, index) in features"
            :key="index"
            class="feature-item"
            :style="{ animationDelay: index * 0.15 + 's' }"
          >
            <div class="feature-icon-wrapper">
              <div class="feature-icon">{{ feature.icon }}</div>
              <div class="icon-glow"></div>
            </div>
            <h3 class="feature-title">{{ feature.title }}</h3>
            <p class="feature-description">{{ feature.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="stats-section" ref="statsSection">
      <div class="container">
        <div class="stats-grid">
          <div
            class="stats-item"
            v-for="(stat, index) in statsData"
            :key="index"
          >
            <div class="stats-icon">{{ stat.icon }}</div>
            <div class="stats-number">{{ stat.value }}</div>
            <div class="stats-label">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <section class="about-section" id="about">
      <div class="container">
        <div class="about-content">
          <div class="about-text">
            <h2 class="section-title">關於CC</h2>
            <div class="title-decoration"></div>
            <p class="about-description">
              我們是專業的遊戲外掛開發團隊，致力於為玩家提供安全、穩定、高效的遊戲輔助工具。
              多年來，我們始終堅持技術創新，為超過萬名玩家提供優質服務。
            </p>
            <div class="about-highlights">
              <div class="highlight-item">
                <span class="highlight-number">3+</span>
                <span class="highlight-text">年專業經驗</span>
              </div>
              <div class="highlight-item">
                <span class="highlight-number">10K+</span>
                <span class="highlight-text">滿意客戶</span>
              </div>
              <div class="highlight-item">
                <span class="highlight-number">24/7</span>
                <span class="highlight-text">技術支援</span>
              </div>
            </div>
          </div>
          <div class="about-visual">
            <div class="tech-circle">
              <div class="circle-ring"></div>
              <div class="circle-core">
                <span class="core-text">About CC</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="contact-section" id="contact">
      <div class="container">
        <div class="section-header">
          <h2 class="section-title">聯絡我們</h2>
          <div class="title-decoration"></div>
          <p class="section-subtitle">有任何問題？我們隨時為您服務</p>
        </div>

        <div class="contact-content">
          <div class="contact-info">
            <div class="contact-item">
              <div class="contact-icon">💬</div>
              <div class="contact-text">
                <h3>即時客服</h3>
                <p>24小時在線客服支援</p>
              </div>
            </div>
            <div class="contact-item">
              <div class="contact-icon">⚡</div>
              <div class="contact-text">
                <h3>快速回應</h3>
                <p>平均5分鐘內回覆</p>
              </div>
            </div>
            <div class="contact-item">
              <div class="contact-icon">🔒</div>
              <div class="contact-text">
                <h3>安全保障</h3>
                <p>專業技術團隊支援</p>
              </div>
            </div>
          </div>

          <div class="contact-cta">
            <a
              href="https://lin.ee/GwsPMQw"
              target="_blank"
              class="line-button"
            >
              <span class="line-icon">💬</span>
              <span class="line-text">點我加LINE</span>
              <span class="line-arrow">→</span>
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style>
/* 恢復成原本漂亮的星星樣式 */
.star {
    position: absolute; 
    background: white;
    border-radius: 50%; 
    animation: twinkle 3s ease-in-out infinite alternate; 
    z-index: 1; /* 確保星星在背景漸層之上，但在主要內容之下 */
}

@keyframes twinkle {
    0% { opacity: 0.3; transform: scale(1); }
    100% { opacity: 1; transform: scale(1.2); }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0px) translateX(0px) rotate(0deg);
    opacity: 0.6;
  }
  50% {
    transform: translateY(-30px) translateX(15px) rotate(180deg);
    opacity: 1;
  }
}
</style>

<script src="@/assets/Js/home.js"></script>

<style scoped src="@/assets/Css/home.css"></style>